import { 
  EmailNotification, 
  SendEmailRequest, 
  PaginationParams
} from '../types';

// Email list response structure to match backend DTO
interface EmailListResponse {
  data: EmailNotification[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Send email notification
export const sendEmailNotification = async (data: SendEmailRequest): Promise<EmailNotification> => {
  const formData = new FormData();
  formData.append('subject', data.subject);
  formData.append('content', data.content);
  formData.append('buildingId', data.buildingId);
  formData.append('sendToAll', data.sendToAll.toString());
  
  // New fields for enhanced notification system
  if (data.deliveryMethods && data.deliveryMethods.length > 0) {
    // Send array elements separately with the same key name
    data.deliveryMethods.forEach(method => {
      formData.append('deliveryMethods[]', method);
    });
  }
  
  if (data.recipientType) {
    formData.append('recipientType', data.recipientType);
  }
  
  if (data.titleImage) {
    formData.append('titleImage', data.titleImage);
  }
  
  if (data.apartmentGroupIds && data.apartmentGroupIds.length > 0) {
    data.apartmentGroupIds.forEach((groupId) => {
      formData.append('apartmentGroupIds[]', groupId);
    });
  } else if (data.apartmentGroupId) {
    formData.append('apartmentGroupId', data.apartmentGroupId);
  }

  if (data.apartmentIds && data.apartmentIds.length > 0) {
    data.apartmentIds.forEach((apartmentId) => {
      formData.append('apartmentIds[]', apartmentId);
    });
  }
  
  if (data.attachments) {
    data.attachments.forEach((file, index) => {
      formData.append(`attachments`, file);
    });
  }

  const response = await fetch(`${API_BASE_URL}/email-notifications`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to send email notification');
  }

  return response.json();
};

// Get email notifications list
export const getEmailNotifications = async (params: PaginationParams = {}): Promise<EmailListResponse> => {
  const searchParams = new URLSearchParams();
  
  if (params.page) searchParams.append('page', params.page.toString());
  if (params.limit) searchParams.append('limit', params.limit.toString());
  if (params.search) searchParams.append('search', params.search);

  const response = await fetch(`${API_BASE_URL}/email-notifications?${searchParams}`, {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch email notifications');
  }

  return response.json();
};

// Get email notification by ID
export const getEmailNotificationById = async (id: string): Promise<EmailNotification> => {
  const response = await fetch(`${API_BASE_URL}/email-notifications/${id}`, {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch email notification');
  }

  return response.json();
};

// Note: Apartment groups are fetched via apartmentsApi.getApartmentGroups() and filtered client-side

// Delete email notification
export const deleteEmailNotification = async (id: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/email-notifications/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete email notification');
  }
}; 